import * as React from 'react';

import {useAppDispatch} from '../../../redux';
import {checkCode} from '../../../redux/actions/login';
import {loginSlice} from '../../../redux/reducers/login';
import {RequestedOperationType} from '../../../shared/models/api';
import {ServerValidationError} from '../../../shared/models/serverValidationError';
import {storage, StorageKeys} from '../../../shared/storage';
import {ConfirmationChangePassword} from './changePassword';
import {ConfirmationInvalidLink} from './invalidLink';
import {Platform} from 'react-native';
import AppRedirectForm from '../../../shared/routing/app-redirect-form';
import {Routes} from '../../../shared/routing';
import {isChangeEmailConfirmation, parseConfirmationQuery} from '../../../shared/hooks/useConfirmationDeepLink';
import {getDeviceType} from '../../../shared/utils/getDeviceType';

interface ConfirmationRoute {
  params: {
    op: string;
    code: string;
    card: string; // not used yet, but comes with query string params
  };
}

const getConfirmationParams = (routeParams: {op?: string; code?: string}) => {
  let op = routeParams.op ?? '';
  let code = routeParams.code ?? '';

  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname === Routes.confirmation) {
    const fromUrl = parseConfirmationQuery(window.location.search);

    op = op || fromUrl.op;
    code = code || fromUrl.code;
  }

  return {op, code};
};

export const Confirmation: React.FC<{route?: ConfirmationRoute}> = ({route: {params: routeParams = {}} = {}}) => {
  const {op, code} = getConfirmationParams(routeParams);
  const dispatch = useAppDispatch();
  const errorMessage = React.useRef(`Возможно, Вы запросили новое\n письмо или ссылка устарела.`);
  const [isCodeChecked, setCodeChecked] = React.useState<boolean | null>(null);
  const deviceType = React.useMemo(() => {
    return getDeviceType();
  }, []);

  React.useEffect(() => {
    if (isChangeEmailConfirmation(op, code)) {
      if (Platform.OS === 'web' && deviceType === 'desktopBrowser') {
        setCodeChecked(false);
      }
      return;
    }

    if (typeof code !== 'string' || !code.length || op.toLowerCase() !== 'restorepassemail') {
      setCodeChecked(false);
    } else {
      (async () => {
        try {
          const codeId = await storage.getItem(StorageKeys.recoveryCodeId);
          if (codeId) {
            dispatch(loginSlice.actions.setSendCodeResponse({codeId}));
            await dispatch(checkCode(code, '', RequestedOperationType.SetPasswordConfirmation));
            setCodeChecked(true);
          } else {
            setCodeChecked(false);
          }
        } catch (e) {
          const error = e as ServerValidationError;
          if (error?.data?.[0]?.message) {
            errorMessage.current = error.data[0].message;
          }
          setCodeChecked(false);
        }
      })();
    }
  }, [op, code]);

  if (Platform.OS === 'web' && deviceType === 'mobileBrowser') {
    if (isChangeEmailConfirmation(op, code)) {
      const search = window.location.search.startsWith('?') ? window.location.search : `?${window.location.search}`;

      return <AppRedirectForm link={`confirmation${search}`} />;
    }

    if (op.toLowerCase() === 'restorepassemail') {
      const search = window.location.search.startsWith('?') ? window.location.search : `?${window.location.search}`;

      return <AppRedirectForm link={`login-confirmation${search}`} />;
    }

    if (op.toLowerCase() === 'createpassemail' && !!code) {
      return <AppRedirectForm link={Routes.profileCreatePassword.replace('/:code?', `/${encodeURIComponent(code)}`)} />;
    }
  }

  if (isCodeChecked === null) {
    return null;
  }

  return isCodeChecked ? (
    <ConfirmationChangePassword code={code} />
  ) : (
    <ConfirmationInvalidLink message={errorMessage.current} />
  );
};
